/**
 * Renderer-independent animal registry contract, version 1.
 *
 * Validation checks data shape and claim/evidence consistency. It does not read
 * files, recalculate hashes, inspect a model, or establish realism. A validated
 * claim is limited to its explicit variants, hashes, renderer, LODs and scope.
 * Artifact IDs are opaque delivered-source identities, not downloadable URLs.
 */
export const ANIMAL_REGISTRY_SCHEMA_VERSION = '1.0.0';
export const ANIMAL_STATUS_DIMENSIONS = Object.freeze([
  'provenance', 'anatomy', 'movement', 'interactions', 'contacts', 'lifecycle', 'presentation',
]);
export const ANIMAL_STATUS_STATES = Object.freeze(['unverified', 'implemented', 'needs_revision', 'validated']);

const ID = /^[a-z0-9][a-z0-9._-]*$/;
const HASH = /^[a-f0-9]{64}$/;
const VERSION = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;
const SAFE_PATH = /^(?:[A-Za-z0-9_-][A-Za-z0-9._-]*\/)*[A-Za-z0-9_-][A-Za-z0-9._-]*$/;
const RUNTIME_SENSITIVE_DIMENSIONS = new Set(['movement', 'interactions', 'contacts', 'lifecycle', 'presentation']);
const OWN = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const fail = (path, message) => { throw new TypeError(`Animal registry ${path}: ${message}`); };
const object = (value, path) => { if (!value || typeof value !== 'object' || Array.isArray(value)) fail(path, 'must be an object'); };
const string = (value, path) => { if (typeof value !== 'string' || !value.trim()) fail(path, 'must be a nonempty string'); };
const id = (value, path) => { string(value, path); if (!ID.test(value)) fail(path, 'must be a lowercase stable ID'); };
const hash = (value, path) => { if (typeof value !== 'string' || !HASH.test(value)) fail(path, 'must be a lowercase SHA-256 hash (64 hexadecimal characters)'); };
const array = (value, path) => { if (!Array.isArray(value)) fail(path, 'must be an array'); };
const required = (value, keys, path) => { object(value, path); for (const key of keys) if (!OWN(value, key)) fail(`${path}.${key}`, 'is required'); };
const allowed = (value, keys, path) => { for (const key of Object.keys(value)) if (!keys.includes(key)) fail(`${path}.${key}`, 'is not a supported field'); };
const oneOf = (value, choices, path) => { if (!choices.includes(value)) fail(path, `must be one of ${choices.join(', ')}`); };
const nonnegative = (value, path) => { if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) fail(path, 'must be a finite nonnegative number'); };
const integer = (value, path) => { nonnegative(value, path); if (!Number.isSafeInteger(value)) fail(path, 'must be a safe nonnegative integer'); };
function strings(value, path, check = string, nonempty = false) {
  array(value, path);
  if (nonempty && !value.length) fail(path, 'must not be empty');
  const seen = new Set();
  value.forEach((item, i) => { check(item, `${path}[${i}]`); if (seen.has(item)) fail(`${path}[${i}]`, `duplicate value ${item}`); seen.add(item); });
}
function safePath(value, path) {
  string(value, path);
  if (!SAFE_PATH.test(value)) fail(path, 'must be a safe normalized relative path without traversal, encoding, query, or absolute prefix');
}
function url(value, path) {
  string(value, path);
  let parsed;
  try { parsed = new URL(value); } catch { fail(path, 'must be an absolute HTTP(S) URL'); }
  if (!['https:', 'http:'].includes(parsed.protocol) || !parsed.hostname || parsed.username || parsed.password) fail(path, 'must be an HTTP(S) URL without credentials');
}
function date(value, path) {
  string(value, path);
  if (!/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2}))?$/.test(value) || !Number.isFinite(Date.parse(value))) fail(path, 'must be an ISO date or timestamp with a timezone');
  const day = value.slice(0, 10);
  if (new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) !== day) fail(path, 'must identify a real calendar date');
}
function jsonSafe(value, path = '$', ancestors = new Set(), depth = 0) {
  if (depth > 80) fail(path, 'exceeds the maximum data nesting depth');
  if (value === null || typeof value === 'boolean' || typeof value === 'string') return;
  if (typeof value === 'number') { if (!Number.isFinite(value)) fail(path, 'nonfinite numbers are not JSON-safe'); return; }
  if (typeof value !== 'object') fail(path, `${typeof value} is not JSON-safe`);
  if (ancestors.has(value)) fail(path, 'cyclic data is not JSON-safe');
  if (!Array.isArray(value) && ![Object.prototype, null].includes(Object.getPrototypeOf(value))) fail(path, 'must contain only plain JSON objects');
  if (Object.getOwnPropertySymbols(value).length) fail(path, 'symbol properties are not JSON-safe');
  ancestors.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) if (!OWN(value, i)) fail(`${path}[${i}]`, 'sparse arrays are not JSON-safe');
  }
  for (const key of Object.getOwnPropertyNames(value)) {
    if (Array.isArray(value) && key === 'length') continue;
    if (Array.isArray(value) && (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= value.length)) fail(`${path}.${key}`, 'non-index array properties are not JSON-safe');
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor.enumerable || !OWN(descriptor, 'value')) fail(`${path}.${key}`, 'must be an enumerable data property');
    if (['__proto__', 'prototype', 'constructor'].includes(key)) fail(`${path}.${key}`, 'unsafe object key');
    if (/path$/i.test(key) && descriptor.value !== null) safePath(descriptor.value, `${path}.${key}`);
    jsonSafe(descriptor.value, `${path}.${key}`, ancestors, depth + 1);
  }
  ancestors.delete(value);
}
function locator(value, path, requireOne = true) {
  if (requireOne && !['path', 'url', 'artifactId'].some(key => OWN(value, key))) fail(path, 'requires path, url, or artifactId');
  if (OWN(value, 'path')) safePath(value.path, `${path}.path`);
  if (OWN(value, 'url')) url(value.url, `${path}.url`);
  if (OWN(value, 'artifactId')) string(value.artifactId, `${path}.artifactId`);
}
function artifact(value, path) {
  required(value, ['sha256', 'availability'], path);
  allowed(value, ['path', 'artifactId', 'sha256', 'availability', 'delivery', 'notes'], path);
  if (OWN(value, 'path') === OWN(value, 'artifactId')) fail(path, 'requires exactly one of path or artifactId');
  locator(value, path); hash(value.sha256, `${path}.sha256`);
  oneOf(value.availability, ['local', 'external', 'unavailable'], `${path}.availability`);
  if (OWN(value, 'delivery')) oneOf(value.delivery, ['bundled-source', 'external-source-package'], `${path}.delivery`);
  if (OWN(value, 'artifactId') && (value.delivery !== 'external-source-package' || value.availability === 'local')) fail(path, 'artifactId requires external-source-package delivery and external or unavailable availability');
  if (OWN(value, 'path') && (value.availability === 'external' || value.delivery === 'external-source-package')) fail(path, 'local paths cannot claim external-source-package delivery');
  if (OWN(value, 'notes')) string(value.notes, `${path}.notes`);
}
function hashMap(value, path, nonempty = false) {
  object(value, path);
  if (nonempty && !Object.keys(value).length) fail(path, 'must name at least one source hash');
  for (const [key, sha256] of Object.entries(value)) { id(key, `${path}.${key}`); hash(sha256, `${path}.${key}`); }
}
function identity(value, path) {
  required(value, ['label', 'taxon'], path);
  allowed(value, ['label', 'taxon', 'notes'], path);
  string(value.label, `${path}.label`);
  if (OWN(value, 'notes')) string(value.notes, `${path}.notes`);
  const p = `${path}.taxon`, taxon = value.taxon;
  required(taxon, ['name', 'rank', 'confidence', 'basis'], p);
  allowed(taxon, ['name', 'rank', 'confidence', 'basis'], p);
  if (taxon.name !== null) string(taxon.name, `${p}.name`);
  string(taxon.rank, `${p}.rank`);
  oneOf(taxon.confidence, ['unverified', 'low', 'medium', 'high'], `${p}.confidence`);
  string(taxon.basis, `${p}.basis`);
  if (taxon.name === null && taxon.confidence !== 'unverified') fail(`${p}.confidence`, 'an unknown taxon must remain unverified');
}
function source(value, path, isTexture) {
  required(value, ['id', 'author', 'license'], path);
  allowed(value, ['id', 'author', 'license', 'url', 'path', 'artifactId', 'sha256', 'label', 'title', 'notes', ...(isTexture ? ['origin'] : [])], path);
  id(value.id, `${path}.id`); string(value.author, `${path}.author`); string(value.license, `${path}.license`);
  locator(value, path);
  if (OWN(value, 'sha256')) hash(value.sha256, `${path}.sha256`);
  if (OWN(value, 'notes')) string(value.notes, `${path}.notes`);
  for (const key of ['label', 'title']) if (OWN(value, key)) string(value[key], `${path}.${key}`);
  if (isTexture) { required(value, ['origin'], path); oneOf(value.origin, ['inherited', 'procedural', 'original', 'photo-derived'], `${path}.origin`); }
}
function measurements(value, path) {
  required(value, ['unit', 'nominal', 'seededBounds', 'method', 'limitations'], path);
  allowed(value, ['unit', 'nominal', 'seededBounds', 'method', 'limitations'], path);
  if (value.unit !== 'm') fail(`${path}.unit`, 'must be m (meters)');
  string(value.method, `${path}.method`); strings(value.limitations, `${path}.limitations`);
  for (const key of ['nominal', 'seededBounds']) object(value[key], `${path}.${key}`);
  for (const [key, measurement] of Object.entries(value.nominal)) { id(key, `${path}.nominal.${key}`); nonnegative(measurement, `${path}.nominal.${key}`); }
  for (const [key, bounds] of Object.entries(value.seededBounds)) {
    const p = `${path}.seededBounds.${key}`;
    if (!OWN(value.nominal, key)) fail(p, 'requires a corresponding nominal measurement');
    array(bounds, p); if (bounds.length !== 2) fail(p, 'must be [minimum, maximum]');
    bounds.forEach((n, i) => nonnegative(n, `${p}[${i}]`));
    if (bounds[0] > bounds[1]) fail(p, 'minimum must not exceed maximum');
    if (value.nominal[key] < bounds[0] || value.nominal[key] > bounds[1]) fail(p, 'must include the nominal measurement');
  }
}
function runtime(value, path) {
  required(value, ['contracts', 'population', 'budgets'], path);
  allowed(value, ['contracts', 'population', 'budgets'], path);
  array(value.contracts, `${path}.contracts`);
  const contracts = new Map(), hashes = new Set();
  value.contracts.forEach((contract, i) => {
    const p = `${path}.contracts[${i}]`;
    required(contract, ['id', 'version', 'sourceHashes', 'notes'], p);
    allowed(contract, ['id', 'version', 'sourceHashes', 'notes'], p);
    id(contract.id, `${p}.id`); unique(contracts, contract, p, 'runtime contract');
    if (typeof contract.version !== 'string' || !VERSION.test(contract.version)) fail(`${p}.version`, 'must be a semantic version');
    hashMap(contract.sourceHashes, `${p}.sourceHashes`, true);
    string(contract.notes, `${p}.notes`);
    Object.values(contract.sourceHashes).forEach(sha256 => hashes.add(sha256));
  });
  required(value.population, ['maximumResidents'], `${path}.population`);
  allowed(value.population, ['maximumResidents'], `${path}.population`);
  integer(value.population.maximumResidents, `${path}.population.maximumResidents`);
  required(value.budgets, ['standardTriangles', 'fallbackTriangles'], `${path}.budgets`);
  allowed(value.budgets, ['standardTriangles', 'fallbackTriangles'], `${path}.budgets`);
  for (const key of ['standardTriangles', 'fallbackTriangles']) integer(value.budgets[key], `${path}.budgets.${key}`);
  return hashes;
}
function refs(value, path, known, check = id, nonempty = false) {
  strings(value, path, check, nonempty);
  for (const ref of value) if (!known.has(ref)) fail(path, `references nonexistent ID or hash ${ref}`);
}
function statusShape(value, path) {
  required(value, ['state', 'evidenceIds'], path);
  allowed(value, ['state', 'evidenceIds', 'reason', 'notApplicableReason', 'validation'], path);
  oneOf(value.state, ANIMAL_STATUS_STATES, `${path}.state`);
  strings(value.evidenceIds, `${path}.evidenceIds`, id);
  for (const key of ['reason', 'notApplicableReason']) if (OWN(value, key)) string(value[key], `${path}.${key}`);
  if (OWN(value, 'notApplicableReason') && (value.state !== 'unverified' || OWN(value, 'validation'))) fail(path, 'not-applicable must use unverified with a reason, never a validation');
  if (value.state === 'validated' && !OWN(value, 'validation')) fail(`${path}.validation`, 'is required for validated status');
  if (value.state !== 'validated' && OWN(value, 'validation')) fail(`${path}.validation`, 'is only allowed for validated status; retain historical proof in evidence');
}
function scope(value, path, validated = false) {
  const keys = ['sourceHashes', 'assetHashes', 'renderer', 'lods', 'variantIds', 'fixtureIds', 'date', 'scope', 'limitations'];
  if (validated) { required(value, keys, path); allowed(value, keys, path); }
  if (OWN(value, 'sourceHashes')) hashMap(value.sourceHashes, `${path}.sourceHashes`, validated);
  if (OWN(value, 'assetHashes')) strings(value.assetHashes, `${path}.assetHashes`, hash, validated);
  for (const key of ['lods', 'variantIds', 'fixtureIds']) if (OWN(value, key)) strings(value[key], `${path}.${key}`, id, validated);
  for (const key of ['renderer', 'scope']) if (OWN(value, key)) string(value[key], `${path}.${key}`);
  if (OWN(value, 'date')) date(value.date, `${path}.date`);
  if (OWN(value, 'limitations')) strings(value.limitations, `${path}.limitations`);
}
function unique(map, value, path, kind) {
  if (map.has(value.id)) fail(`${path}.id`, `duplicate ${kind} ID ${value.id}`);
  map.set(value.id, value);
}

/** Return the original JSON-safe data, unchanged, or throw a path-specific TypeError. */
export function validateAnimalRegistry(data) {
  jsonSafe(data);
  required(data, ['schemaVersion', 'registryVersion', 'release', 'entries', 'evidence'], '$');
  allowed(data, ['$schema', 'schemaVersion', 'registryVersion', 'release', 'entries', 'evidence', 'notes'], '$');
  if (OWN(data, '$schema')) url(data.$schema, '$.$schema');
  if (data.schemaVersion !== ANIMAL_REGISTRY_SCHEMA_VERSION) fail('$.schemaVersion', `unsupported version; expected ${ANIMAL_REGISTRY_SCHEMA_VERSION}`);
  if (typeof data.registryVersion !== 'string' || !VERSION.test(data.registryVersion)) fail('$.registryVersion', 'must be a semantic version');
  required(data.release, ['id', 'sourceId', 'sourceRevision'], '$.release');
  allowed(data.release, ['id', 'sourceId', 'sourceRevision', 'notes'], '$.release');
  id(data.release.id, '$.release.id'); string(data.release.sourceId, '$.release.sourceId'); string(data.release.sourceRevision, '$.release.sourceRevision');
  if (OWN(data.release, 'notes')) string(data.release.notes, '$.release.notes');
  if (OWN(data, 'notes')) strings(data.notes, '$.notes');
  array(data.entries, '$.entries'); array(data.evidence, '$.evidence');
  if (!data.entries.length) fail('$.entries', 'must contain at least one family');
  const entries = new Map(), families = new Map(), variants = new Map(), evidence = new Map(), payloads = new Map();
  const contexts = [];
  data.entries.forEach((entry, i) => {
    const p = `$.entries[${i}]`;
    required(entry, ['id', 'family', 'identity', 'habitat', 'provenance', 'measurements', 'rig', 'behaviors', 'runtime', 'variants', 'status'], p);
    allowed(entry, ['id', 'family', 'identity', 'habitat', 'provenance', 'measurements', 'rig', 'behaviors', 'runtime', 'variants', 'status', 'notes'], p);
    id(entry.id, `${p}.id`); id(entry.family, `${p}.family`); unique(entries, entry, p, 'entry');
    if (families.has(entry.family)) fail(`${p}.family`, `duplicate family ${entry.family}; place variants inside one family entry`);
    families.set(entry.family, entry);
    identity(entry.identity, `${p}.identity`); strings(entry.habitat, `${p}.habitat`);
    if (OWN(entry, 'notes')) strings(entry.notes, `${p}.notes`);
    required(entry.provenance, ['models', 'references', 'textures', 'runtime'], `${p}.provenance`);
    allowed(entry.provenance, ['models', 'references', 'textures', 'runtime'], `${p}.provenance`);
    const sources = new Map(), sourceHashes = new Set(), assetHashes = new Set(), variantIds = new Set(), lodIds = new Set();
    for (const key of ['models', 'references', 'textures', 'runtime']) {
      array(entry.provenance[key], `${p}.provenance.${key}`);
      entry.provenance[key].forEach((item, n) => { const sp = `${p}.provenance.${key}[${n}]`; source(item, sp, key === 'textures'); unique(sources, item, sp, 'source'); if (item.sha256) sourceHashes.add(item.sha256); });
    }
    measurements(entry.measurements, `${p}.measurements`);
    const runtimeHashes = runtime(entry.runtime, `${p}.runtime`);
    runtimeHashes.forEach(sha256 => sourceHashes.add(sha256));
    const rig = entry.rig, rp = `${p}.rig`;
    required(rig, ['id', 'type', 'roles', 'actions'], rp); allowed(rig, ['id', 'type', 'roles', 'actions'], rp);
    id(rig.id, `${rp}.id`); oneOf(rig.type, ['skinned', 'rigid-parts', 'procedural', 'none', 'mixed'], `${rp}.type`);
    strings(rig.roles, `${rp}.roles`, id); array(rig.actions, `${rp}.actions`);
    const actions = new Map();
    rig.actions.forEach((action, n) => {
      const ap = `${rp}.actions[${n}]`;
      required(action, ['id', 'kind', 'description', 'sourceIds'], ap); allowed(action, ['id', 'kind', 'description', 'sourceIds', 'label', 'notes'], ap);
      for (const key of ['label', 'notes']) if (OWN(action, key)) string(action[key], `${ap}.${key}`);
      id(action.id, `${ap}.id`); oneOf(action.kind, ['clip', 'procedural', 'pose'], `${ap}.kind`); string(action.description, `${ap}.description`);
      refs(action.sourceIds, `${ap}.sourceIds`, sources); unique(actions, action, ap, 'action');
    });
    const bp = `${p}.behaviors`, behaviorIds = new Map();
    required(entry.behaviors, ['gaits', 'environment', 'neighbors'], bp); allowed(entry.behaviors, ['gaits', 'environment', 'neighbors'], bp);
    for (const key of ['gaits', 'environment', 'neighbors']) {
      array(entry.behaviors[key], `${bp}.${key}`);
      entry.behaviors[key].forEach((behavior, n) => {
        const b = `${bp}.${key}[${n}]`;
        required(behavior, ['id', 'state', 'description', 'actionIds', 'sourceIds', 'evidenceIds'], b);
        allowed(behavior, ['id', 'state', 'description', 'actionIds', 'sourceIds', 'evidenceIds', 'notApplicableReason', 'label', 'notes'], b);
        for (const key of ['label', 'notes']) if (OWN(behavior, key)) string(behavior[key], `${b}.${key}`);
        id(behavior.id, `${b}.id`); unique(behaviorIds, behavior, b, 'behavior');
        // Behavior inventory describes implementation. Validation belongs to the dimension's hash-scoped status.
        oneOf(behavior.state, ['unverified', 'implemented', 'needs_revision'], `${b}.state`);
        string(behavior.description, `${b}.description`); refs(behavior.actionIds, `${b}.actionIds`, actions); refs(behavior.sourceIds, `${b}.sourceIds`, sources); strings(behavior.evidenceIds, `${b}.evidenceIds`, id);
        if (OWN(behavior, 'notApplicableReason')) { string(behavior.notApplicableReason, `${b}.notApplicableReason`); if (behavior.state !== 'unverified') fail(b, 'not-applicable behavior must be unverified with a reason'); }
      });
    }
    array(entry.variants, `${p}.variants`); if (!entry.variants.length) fail(`${p}.variants`, 'must contain at least one variant');
    entry.variants.forEach((variant, n) => {
      const v = `${p}.variants[${n}]`;
      required(variant, ['id', 'family', 'label', 'assets', 'selectedLods'], v); allowed(variant, ['id', 'family', 'label', 'identity', 'assets', 'selectedLods', 'notes'], v);
      id(variant.id, `${v}.id`); id(variant.family, `${v}.family`); string(variant.label, `${v}.label`);
      if (variant.family !== entry.family) fail(`${v}.family`, `family/variant mismatch: expected ${entry.family}`);
      unique(variants, variant, v, 'variant'); variantIds.add(variant.id);
      if (OWN(variant, 'identity')) identity(variant.identity, `${v}.identity`);
      if (OWN(variant, 'notes')) strings(variant.notes, `${v}.notes`);
      const assets = variant.assets, a = `${v}.assets`;
      required(assets, ['blender', 'export', 'lods'], a); allowed(assets, ['blender', 'export', 'lods'], a);
      if (assets.blender !== null) { artifact(assets.blender, `${a}.blender`); sourceHashes.add(assets.blender.sha256); }
      {
        required(assets.export, ['contractId', 'contractVersion', 'tool', 'version', 'recipe'], `${a}.export`);
        allowed(assets.export, ['contractId', 'contractVersion', 'tool', 'version', 'recipe', 'notes'], `${a}.export`);
        id(assets.export.contractId, `${a}.export.contractId`);
        if (typeof assets.export.contractVersion !== 'string' || !VERSION.test(assets.export.contractVersion)) fail(`${a}.export.contractVersion`, 'must be a semantic version');
        string(assets.export.tool, `${a}.export.tool`); string(assets.export.version, `${a}.export.version`);
        if (assets.export.recipe !== null) { artifact(assets.export.recipe, `${a}.export.recipe`); sourceHashes.add(assets.export.recipe.sha256); }
        if (OWN(assets.export, 'notes')) string(assets.export.notes, `${a}.export.notes`);
      }
      array(assets.lods, `${a}.lods`); if (!assets.lods.length) fail(`${a}.lods`, 'must contain at least one LOD');
      const localLods = new Map();
      assets.lods.forEach((lod, l) => {
        const lp = `${a}.lods[${l}]`;
        required(lod, ['id', 'payloadId', 'path', 'sha256', 'bytes', 'purpose', 'rigId', 'exportContractId'], lp);
        allowed(lod, ['id', 'payloadId', 'path', 'sha256', 'bytes', 'purpose', 'rigId', 'exportContractId', 'triangles', 'rigContractId'], lp);
        string(lod.purpose, `${lp}.purpose`);
        if (OWN(lod, 'triangles')) integer(lod.triangles, `${lp}.triangles`);
        if (OWN(lod, 'rigContractId')) id(lod.rigContractId, `${lp}.rigContractId`);
        for (const key of ['id', 'payloadId', 'rigId', 'exportContractId']) id(lod[key], `${lp}.${key}`);
        unique(localLods, lod, lp, 'LOD'); lodIds.add(lod.id); safePath(lod.path, `${lp}.path`); hash(lod.sha256, `${lp}.sha256`); integer(lod.bytes, `${lp}.bytes`);
        if (lod.bytes === 0) fail(`${lp}.bytes`, 'must be greater than zero');
        if (lod.rigId !== rig.id) fail(`${lp}.rigId`, `must reference the entry rig ${rig.id}`);
        if (!assets.export || lod.exportContractId !== assets.export.contractId) fail(`${lp}.exportContractId`, 'must reference the variant export contract');
        if (payloads.has(lod.payloadId)) fail(`${lp}.payloadId`, `duplicate payload ID ${lod.payloadId}`);
        payloads.set(lod.payloadId, lod); assetHashes.add(lod.sha256);
      });
      required(variant.selectedLods, ['standard', 'fallback'], `${v}.selectedLods`);
      allowed(variant.selectedLods, ['standard', 'fallback'], `${v}.selectedLods`);
      for (const key of ['standard', 'fallback']) {
        id(variant.selectedLods[key], `${v}.selectedLods.${key}`);
        if (!localLods.has(variant.selectedLods[key])) fail(`${v}.selectedLods.${key}`, 'must reference a LOD on this variant');
      }
    });
    required(entry.status, ANIMAL_STATUS_DIMENSIONS, `${p}.status`); allowed(entry.status, ANIMAL_STATUS_DIMENSIONS, `${p}.status`);
    for (const dimension of ANIMAL_STATUS_DIMENSIONS) statusShape(entry.status[dimension], `${p}.status.${dimension}`);
    contexts.push({ entry, p, sources, sourceHashes, runtimeHashes, assetHashes, variantIds, lodIds });
  });
  for (const entryId of entries.keys()) if (variants.has(entryId)) fail('$.entries', `entry and variant IDs must be distinct: ${entryId}`);
  data.evidence.forEach((item, i) => {
    const p = `$.evidence[${i}]`;
    required(item, ['id', 'kind', 'family', 'variantIds', 'scope'], p);
    allowed(item, ['id', 'kind', 'family', 'variantIds', 'scope', 'dimensions', 'path', 'url', 'artifactId', 'sha256', 'sourceHashes', 'assetHashes', 'renderer', 'lods', 'fixtureIds', 'date', 'limitations', 'notes'], p);
    id(item.id, `${p}.id`); string(item.kind, `${p}.kind`); id(item.family, `${p}.family`); string(item.scope, `${p}.scope`); unique(evidence, item, p, 'evidence'); locator(item, p);
    if (!families.has(item.family)) fail(`${p}.family`, `references nonexistent family ${item.family}`);
    strings(item.variantIds, `${p}.variantIds`, id, true);
    for (const variantId of item.variantIds) if (!variants.has(variantId) || variants.get(variantId).family !== item.family) fail(`${p}.variantIds`, `family/variant mismatch for ${variantId}`);
    if (OWN(item, 'sha256')) hash(item.sha256, `${p}.sha256`);
    if (OWN(item, 'notes')) string(item.notes, `${p}.notes`);
    if (OWN(item, 'dimensions')) strings(item.dimensions, `${p}.dimensions`, (value, key) => oneOf(value, ANIMAL_STATUS_DIMENSIONS, key), true);
    scope(item, p);
  });
  for (const context of contexts) {
    const {entry, p, sourceHashes, runtimeHashes, assetHashes, variantIds, lodIds} = context;
    const entryEvidence = new Map([...evidence].filter(([, item]) => item.family === entry.family));
    for (const key of ['gaits', 'environment', 'neighbors']) entry.behaviors[key].forEach((behavior, i) => refs(behavior.evidenceIds, `${p}.behaviors.${key}[${i}].evidenceIds`, entryEvidence));
    for (const dimension of ANIMAL_STATUS_DIMENSIONS) {
      const status = entry.status[dimension], sp = `${p}.status.${dimension}`;
      refs(status.evidenceIds, `${sp}.evidenceIds`, entryEvidence, id, status.state === 'validated');
      if (status.state !== 'validated') continue;
      const validation = status.validation, vp = `${sp}.validation`;
      scope(validation, vp, true);
      for (const [name, sha256] of Object.entries(validation.sourceHashes)) if (!sourceHashes.has(sha256)) fail(`${vp}.sourceHashes.${name}`, 'references a nonexistent or stale source hash');
      if (RUNTIME_SENSITIVE_DIMENSIONS.has(dimension)) {
        if (!runtimeHashes.size) fail(`${vp}.sourceHashes`, `${dimension} validation requires a declared runtime contract with source hashes`);
        for (const sha256 of runtimeHashes) if (!Object.values(validation.sourceHashes).includes(sha256)) fail(`${vp}.sourceHashes`, `missing current runtime contract source hash ${sha256} for ${dimension}`);
      }
      refs(validation.assetHashes, `${vp}.assetHashes`, assetHashes, hash, true);
      refs(validation.variantIds, `${vp}.variantIds`, variantIds, id, true);
      refs(validation.lods, `${vp}.lods`, lodIds, id, true);
      if (validation.variantIds.length !== variantIds.size) fail(`${vp}.variantIds`, 'family-level validation must cover every variant; partial evidence remains implemented');
      // Every selected variant/LOD must be represented by its current exact asset hash.
      const expectedAssets = new Set();
      for (const variantId of validation.variantIds) {
        const variant = variants.get(variantId);
        for (const lodId of validation.lods) {
          const lod = variant.assets.lods.find(item => item.id === lodId);
          if (!lod || !validation.assetHashes.includes(lod.sha256)) fail(vp, `missing current asset hash for ${variantId}/${lodId}`);
          expectedAssets.add(lod.sha256);
        }
        if (variant.assets.blender && !Object.values(validation.sourceHashes).includes(variant.assets.blender.sha256)) fail(`${vp}.sourceHashes`, `missing current Blender source hash for ${variantId}`);
        if (variant.assets.export.recipe && !Object.values(validation.sourceHashes).includes(variant.assets.export.recipe.sha256)) fail(`${vp}.sourceHashes`, `missing current export recipe hash for ${variantId}`);
      }
      if (validation.assetHashes.length !== expectedAssets.size) fail(`${vp}.assetHashes`, 'must contain exactly the hashes of the scoped variants and LODs');
      // Individually scoped proofs may combine to validate a family. Every cited
      // proof must match this current claim, and the union must cover it entirely.
      const covered = { sourceHashes: new Set(), assetHashes: new Set(), variantIds: new Set(), lods: new Set(), fixtureIds: new Set() };
      for (const evidenceId of status.evidenceIds) {
        const proof = evidence.get(evidenceId), ep = `${sp}.evidenceIds (${evidenceId})`;
        scope(proof, ep);
        if (!proof.dimensions?.includes(dimension)) fail(ep, `evidence must explicitly cover the ${dimension} dimension`);
        for (const key of ['assetHashes', 'variantIds', 'lods', 'fixtureIds']) {
          if (!Array.isArray(proof[key]) || !proof[key].length || !proof[key].every(item => validation[key].includes(item))) fail(ep, `evidence ${key} must be nonempty and within the validation scope`);
          proof[key].forEach(item => covered[key].add(item));
        }
        if (!proof.sourceHashes || !Object.keys(proof.sourceHashes).length) fail(ep, 'evidence requires named sourceHashes');
        for (const [name, sha256] of Object.entries(proof.sourceHashes)) {
          if (validation.sourceHashes[name] !== sha256) fail(ep, `evidence sourceHashes.${name} must match the current validation source hash`);
          covered.sourceHashes.add(name);
        }
        for (const key of ['renderer', 'scope']) if (proof[key] !== validation[key]) fail(ep, `evidence ${key} must match validation ${key}`);
        if (!proof.date || Date.parse(proof.date) > Date.parse(validation.date)) fail(ep, 'evidence must be dated no later than validation');
        if (!Array.isArray(proof.limitations)) fail(ep, 'validated evidence must declare limitations');
        if (!proof.limitations.every(item => validation.limitations.includes(item))) fail(ep, 'validation must retain evidence limitations');
        for (const variantId of proof.variantIds) for (const lodId of proof.lods) {
          const lod = variants.get(variantId).assets.lods.find(item => item.id === lodId);
          if (!lod || !proof.assetHashes.includes(lod.sha256)) fail(ep, `evidence is missing the current asset hash for ${variantId}/${lodId}`);
        }
      }
      for (const key of ['assetHashes', 'variantIds', 'lods', 'fixtureIds']) if (!validation[key].every(item => covered[key].has(item))) fail(sp, `evidence does not cover all validation ${key}`);
      if (!Object.keys(validation.sourceHashes).every(name => covered.sourceHashes.has(name))) fail(sp, 'evidence does not cover all validation sourceHashes');
    }
  }
  return data;
}
